import { useState, useEffect, useCallback } from 'react';
import '@/App.css';
import api from '@/api';
import { useAuth } from '@/AuthContext';
import AuthPage from '@/AuthPage';
import IntegrationsPanel from '@/IntegrationsPanel';
import DetailModal from '@/DetailModal';
import DataIndexPanel from '@/DataIndexPanel';
import OnCallReadinessPanel from '@/OnCallReadinessPanel';
import SlackIntegrationPanel from '@/SlackIntegrationPanel';
import IntegrationsHub from '@/IntegrationsHub';
import DataSyncPanel from '@/DataSyncPanel';
import UnifiedSearch from '@/UnifiedSearch';
import SyncStatusBar from '@/SyncStatusBar';
import CausalChainAnalysis from '@/CausalChainAnalysis';
import InvestigationPage from '@/InvestigationPage';
import HistoryPanel from '@/HistoryPanel';
import UserProfilePage from '@/UserProfilePage';
import Sidebar from '@/Sidebar';
import {
  createInvestigationHistory,
  updateInvestigationHistory,
} from '@/services/historyService';
import { Search, AlertCircle, ArrowRight, Copy, CheckCircle, Activity, User as UserIcon, LogOut, TrendingUp, ExternalLink, FileText, MessageSquare, Code, Shield, Hash, Link2, RefreshCw, Sparkles, History, Settings, PanelLeft } from 'lucide-react';

function MainApp() {
  const { user, logout } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [copiedCommand, setCopiedCommand] = useState(null);
  const [showCode, setShowCode] = useState(true);
  const [activeView, setActiveView] = useState('graph');
  const [codeViewRepoKey, setCodeViewRepoKey] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showGlobalHistory, setShowGlobalHistory] = useState(false);
  const [pendingSearchRestore, setPendingSearchRestore] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [slackConnectionMessage, setSlackConnectionMessage] = useState(null);
  const [autoOpenConnect, setAutoOpenConnect] = useState(null);  // 'github' or 'slack' to auto-open modal

  // Causal Chain Analysis state
  const [showCausalChainAnalysis, setShowCausalChainAnalysis] = useState(false);
  const [causalChainData, setCausalChainData] = useState(null);
  const [causalChainLoading, setCausalChainLoading] = useState(false);

  // Investigation page state
  const [currentInvestigationId, setCurrentInvestigationId] = useState(null);
  const [currentInvestigationItem, setCurrentInvestigationItem] = useState(null);
  const [activeInvestigationHistoryId, setActiveInvestigationHistoryId] = useState(null);

  // Handle URL parameters (for OAuth callbacks, etc.)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    const slackConnected = params.get('slack_connected');
    const workspace = params.get('workspace');
    const slackError = params.get('error');
    const errorMessage = params.get('message');

    // Check for investigation route: /investigations/:id
    const pathname = window.location.pathname;
    const investigationMatch = pathname.match(/^\/investigations\/([a-f0-9-]+)$/i);
    if (investigationMatch) {
      const investigationId = investigationMatch[1];
      setCurrentInvestigationId(investigationId);
      setActiveView('investigation');
    }

    // Navigate to view if specified
    if (view) {
      setActiveView(view);
    }

    // Show Slack connection success message
    if (slackConnected === 'true' && workspace) {
      setSlackConnectionMessage({
        type: 'success',
        message: `Successfully connected to ${decodeURIComponent(workspace)}! Auto-syncing incident channels...`
      });
      // Clear the message after 5 seconds
      setTimeout(() => setSlackConnectionMessage(null), 5000);
    }

    // Show Slack error message
    if (slackError) {
      setSlackConnectionMessage({
        type: 'error',
        message: errorMessage ? decodeURIComponent(errorMessage) : 'Failed to connect Slack'
      });
      setTimeout(() => setSlackConnectionMessage(null), 8000);
    }

    // Clean up URL parameters (but not investigation routes)
    if ((view || slackConnected || slackError) && !investigationMatch) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Navigate to investigation page (called from history panel)
  const navigateToInvestigation = (investigationId, historyItem) => {
    setCurrentInvestigationId(investigationId);
    setCurrentInvestigationItem(historyItem);
    setActiveView('investigation');
    window.history.pushState({}, '', `/investigations/${investigationId}`);
  };

  // Handle back from investigation page
  const handleBackFromInvestigation = () => {
    setCurrentInvestigationId(null);
    setCurrentInvestigationItem(null);
    setActiveView('search');
    window.history.pushState({}, '', '/');
  };

  // Old searchHistory loading removed - now using unified history service

  const [scannerData, setScannerData] = useState(null);

  // Helper to detect if query is a question (should trigger AI mode)
  const isQuestionQuery = (q) => {
    if (!q) return false;
    const trimmed = q.trim().toLowerCase();
    // Contains question mark
    if (trimmed.includes('?')) return true;
    // Starts with question words
    const questionStarters = ['what', 'why', 'how', 'when', 'where', 'which', 'who', 'is', 'are', 'can', 'could', 'would', 'should', 'does', 'do', 'will', 'explain', 'describe', 'tell me', 'help me', 'investigate', 'analyze', 'find out'];
    return questionStarters.some(starter => trimmed.startsWith(starter + ' ') || trimmed === starter);
  };

  const handleInvestigate = async (e) => {
    if (e) e.preventDefault();

    // Auto-detect questions and route to AI analysis
    if (isQuestionQuery(query)) {
      handleCausalChainInvestigation(query);
      return;
    }

    setLoading(true);
    setError(null);
    setSearchResults(null);
    setScannerData(null);

    try {
      // First, try code search with the query
      if (query && query.trim()) {
        const codeSearchResponse = await api.post(`/integrations/code-search?query=${encodeURIComponent(query)}&top_k=3`);
        setSearchResults(codeSearchResponse.data);
        setActiveView('graph');
      }

      // Load incident analysis with scanner data
      try {
        const graphResponse = await api.get('/incidents/INC-2025-1204-CRITICAL/graph-with-scanner');
        setAnalysis(graphResponse.data.analysis);
        setScannerData(graphResponse.data.scanner);
      } catch (scannerErr) {
        // Fallback to regular root cause if scanner endpoint fails
        console.warn('Scanner data not available, using regular analysis:', scannerErr);
        const response = await api.get('/incidents/INC-2025-1204-CRITICAL/root-cause');
        setAnalysis(response.data);
      }

      // Reload search history
      setTimeout(loadSearchHistory, 1000);
    } catch (err) {
      console.error('Investigation error:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
        logout();
      } else {
        setError(err.response?.data?.detail || 'Failed to investigate incident');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle LLM-powered causal chain investigation
  const handleCausalChainInvestigation = async (investigateQuery) => {
    const searchQuery = investigateQuery || query;
    if (!searchQuery.trim()) return;

    // Switch to investigation view immediately
    setActiveView('investigation');

    // Create investigation history record at start
    let historyId = null;
    const startTime = Date.now();
    try {
      const historyResult = await createInvestigationHistory({
        title: searchQuery.trim().slice(0, 100),
        input: {
          type: 'query',
          query: searchQuery.trim(),
        },
        status: 'inProgress',
        restorationData: { query: searchQuery.trim() },
      });
      historyId = historyResult.id;
      setCurrentInvestigationId(historyId);
      setCurrentInvestigationItem(historyResult.item); // Set initial state (unwrapped)
      setActiveInvestigationHistoryId(historyId);
    } catch (histErr) {
      console.warn('Failed to create investigation history:', histErr);
      // Even if history creation fails, we might want to show something?
      // For now, let's assume it works or we handle it.
    }

    try {
      const response = await api.post('/investigate/llm', { query: searchQuery });
      // setCausalChainData(response.data); // Legacy state

      // Update investigation history with results
      if (historyId) {
        const durationMs = Date.now() - startTime;
        const updatedItem = {
          status: 'completed',
          completedAt: new Date().toISOString(),
          stepsMeta: {
            numberOfSteps: 1,
            durationMs,
          },
          outputsMeta: {
            summary: response.data.natural_language_summary?.slice(0, 500),
            rootCause: response.data.analysis?.root_cause_summary,
            keyEntities: response.data.searched_sources,
          },
          restorationData: {
            query: searchQuery.trim(),
            result: response.data,
            graph: response.data.graph || response.data.analysis?.graph // Ensure graph is preserved
          },
        };

        try {
          await updateInvestigationHistory(historyId, updatedItem);

          // CRITICAL: Update local state so UI reflects completion
          setCurrentInvestigationItem(prev => ({
            ...prev,
            ...updatedItem
          }));

        } catch (updateErr) {
          console.warn('Failed to update investigation history:', updateErr);
        }
      }
    } catch (err) {
      console.error('Causal chain investigation failed:', err);

      const errorItem = {
        status: 'failed',
        completedAt: new Date().toISOString(),
        outputsMeta: {
          summary: "Investigation failed: " + (err.response?.data?.detail || err.message)
        }
      };

      // Update investigation history as failed
      if (historyId) {
        try {
          await updateInvestigationHistory(historyId, errorItem);
          setCurrentInvestigationItem(prev => ({
            ...prev,
            ...errorItem
          }));
        } catch (updateErr) {
          console.warn('Failed to update investigation history:', updateErr);
        }
      }
    }
  };

  // Handle follow-up questions in causal chain analysis
  const handleAskFollowUp = async (question) => {
    try {
      const response = await api.post('/investigate/ask', {
        question,
        context: causalChainData
      });
      return response.data.answer;
    } catch (err) {
      console.error('Follow-up question failed:', err);
      return `Error: ${err.response?.data?.detail || 'Failed to get answer'}`;
    }
  };

  const copyToClipboard = (text, commandId) => {
    navigator.clipboard.writeText(text).catch(() => { });
    setCopiedCommand(commandId);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60);
    if (diff < 60) return `${diff} minutes ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)} hours ago`;
    return `${Math.floor(diff / 1440)} days ago`;
  };



  return (
    <div className="min-h-screen bg-background bg-grid flex">
      {/* Fixed Enterprise Navigation Panel */}
      <Sidebar
        activeView={activeView}
        onNavigate={setActiveView}
        user={user}
        onLogout={logout}
        onRestoreSearch={(item) => {
          setQuery(item.queryText || item.query || item.title);
          setActiveView('search');
          setPendingSearchRestore(item);
        }}
        onNavigateToInvestigation={navigateToInvestigation}
      />
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header - Clean design */}
        <header className="panel-header relative z-40">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-foreground tracking-tight">
                {activeView === 'search' && 'Unified Search'}
                {activeView === 'graph' && 'Investigation'}
                {activeView === 'code' && 'Data Graph'}
                {activeView === 'oncall' && 'Oncall-Scann'}
                {activeView === 'integrations' && 'Data Connect'}
                {activeView === 'sync' && 'Data Sync'}
                {activeView === 'investigation' && 'Investigation'}
                {activeView === 'history' && 'Data History'}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative z-[100]">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2.5 px-3 py-2 bg-secondary hover:bg-secondary/80 rounded-lg transition-all border border-border"
                  data-testid="user-menu-button"
                >
                  <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
                    <UserIcon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-foreground">{user?.full_name}</span>
                </button>

                {showUserMenu && (
                  <>
                    {/* Backdrop to close on outside click */}
                    <div
                      className="fixed inset-0 z-[99]"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="fixed top-14 right-6 w-72 bg-card rounded-xl shadow-xl border border-border py-2 z-[100] animate-scale-in">
                      <div className="px-4 py-3 border-b border-border">
                        <div className="text-sm font-semibold text-foreground">{user?.full_name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{user?.email}</div>
                        {user?.company && <div className="text-xs text-muted-foreground/70 mt-1">{user.company}</div>}
                      </div>
                      <div className="px-3 py-2 border-b border-border">
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowGlobalHistory(true);
                          }}
                          className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-secondary rounded-lg text-sm transition-colors"
                          data-testid="open-history-button"
                        >
                          <div className="flex items-center gap-2.5">
                            <History className="w-4 h-4 text-muted-foreground" />
                            <span className="text-foreground font-medium">View History</span>
                          </div>
                          <span className="text-xs text-primary font-medium">Searches</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setActiveView('settings');
                          }}
                          className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-secondary rounded-lg text-sm transition-colors"
                          data-testid="open-settings-button"
                        >
                          <div className="flex items-center gap-2.5">
                            <Settings className="w-4 h-4 text-muted-foreground" />
                            <span className="text-foreground font-medium">Settings</span>
                          </div>
                          <span className="text-xs text-muted-foreground">Organization</span>
                        </button>
                      </div>
                      <div className="px-3 py-2">
                        <button
                          onClick={logout}
                          className="w-full px-3 py-2.5 text-left text-sm text-destructive hover:bg-destructive/5 rounded-lg flex items-center gap-2.5 transition-colors font-medium"
                          data-testid="logout-button"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Sync Status Bar - shows when data is being processed */}
        <SyncStatusBar />

        {/* Slack Connection Notification */}
        {slackConnectionMessage && (
          <div className={`px-5 py-3.5 flex items-center justify-between animate-fade-in-down ${slackConnectionMessage.type === 'success'
            ? 'bg-success/5 border-b border-success/10'
            : 'bg-destructive/5 border-b border-destructive/10'
            }`}>
            <div className="flex items-center gap-2.5">
              {slackConnectionMessage.type === 'success' ? (
                <div className="w-6 h-6 rounded-full bg-success flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              ) : (
                <div className="w-6 h-6 rounded-full bg-destructive flex items-center justify-center">
                  <AlertCircle className="w-4 h-4 text-white" />
                </div>
              )}
              <span className={`text-sm font-medium ${slackConnectionMessage.type === 'success' ? 'text-success' : 'text-destructive'}`}>
                {slackConnectionMessage.message}
              </span>
            </div>
            <button
              onClick={() => setSlackConnectionMessage(null)}
              className="w-6 h-6 rounded-lg hover:bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              ×
            </button>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">

            {/* Search View */}
            {activeView === 'search' && (
              <UnifiedSearch
                onNavigateToIntegrations={() => setActiveView('integrations')}
                onNavigateToInvestigation={navigateToInvestigation}
                onInvestigate={handleCausalChainInvestigation}
                pendingSearchRestore={pendingSearchRestore}
                onSearchRestored={() => setPendingSearchRestore(null)}
              />
            )}

            {/* Investigation Detail View */}
            {activeView === 'investigation' && currentInvestigationId && (
              <InvestigationPage
                investigationId={currentInvestigationId}
                historyItem={currentInvestigationItem}
                onBack={handleBackFromInvestigation}
                onRerunInvestigation={(item) => {
                  // Re-run the investigation with the original query
                  if (item.input?.query) {
                    handleBackFromInvestigation();
                    setQuery(item.input.query);
                    handleCausalChainInvestigation(item.input.query);
                  }
                }}
              />
            )}

            {/* Data Index View */}
            {activeView === 'code' && (
              <DataIndexPanel
                onNavigateToIntegrations={() => setActiveView('integrations')}
                onNavigateToSearch={(sourceType) => {
                  setPendingSearchRestore({
                    queryText: '',
                    filters: { sources: [sourceType] }
                  });
                  setActiveView('search');
                }}
              />
            )}

            {/* OnCall Readiness View */}
            {activeView === 'oncall' && (
              <OnCallReadinessPanel
                onNavigateToCodeGraph={(repoKey) => {
                  setCodeViewRepoKey(repoKey);
                  setActiveView('code');
                }}
              />
            )}

            {/* Slack Integration View (legacy) */}
            {activeView === 'slack' && (
              <SlackIntegrationPanel
                onInvestigate={(query) => {
                  setQuery(query);
                  setActiveView('graph');
                  setTimeout(() => handleInvestigate(), 100);
                }}
              />
            )}

            {/* Integrations Hub View */}
            {activeView === 'integrations' && (
              <IntegrationsHub
                onNavigateToSync={() => setActiveView('sync')}
                autoOpenConnect={autoOpenConnect}
                onAutoOpenHandled={() => setAutoOpenConnect(null)}
              />
            )}

            {/* Data Sync View */}
            {activeView === 'sync' && (
              <DataSyncPanel
                onNavigateToIntegrations={(provider) => {
                  if (provider) setAutoOpenConnect(provider);
                  setActiveView('integrations');
                }}
                onInvestigate={(query) => {
                  setQuery(query);
                  setActiveView('graph');
                  setTimeout(() => handleInvestigate(), 100);
                }}
              />
            )}

            {/* Settings View */}
            {activeView === 'settings' && (
              <UserProfilePage
                onBack={() => setActiveView('search')}
              />
            )}

            {/* Graph View */}
            {activeView === 'graph' && (
              <>
                {/* AI Investigation Results - Show inline when active */}
                {showCausalChainAnalysis && (
                  <CausalChainAnalysis
                    isOpen={showCausalChainAnalysis}
                    onClose={() => {
                      setShowCausalChainAnalysis(false);
                      setCausalChainData(null);
                    }}
                    investigationData={causalChainData}
                    onAskFollowUp={handleAskFollowUp}
                  />
                )}

                {/* Graph Visualization - Show when no investigation is active */}
                {!showCausalChainAnalysis && analysis && (
                  <div className="card p-8 mb-6">
                    <h3 className="text-lg font-semibold mb-6 flex items-center gap-2 text-foreground">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      Causal Chain Analysis
                    </h3>
                    <div className="relative" style={{ minHeight: '400px' }}>
                      <GraphVisualization
                        analysis={analysis}
                        scannerData={scannerData}
                        onNodeClick={setSelectedNode}
                      />
                    </div>
                  </div>
                )}

                {/* Loading indicator for AI investigation */}
                {causalChainLoading && !showCausalChainAnalysis && (
                  <div className="card p-8 mb-6 animate-fade-in">
                    <div className="flex items-center gap-5">
                      <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">Analyzing your query...</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">Building causal chain and searching connected systems</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* AI Investigation Results Dashboard */}
                {!showCausalChainAnalysis && searchResults && (
                  <div className="card p-8 mb-6 animate-fade-in">
                    <div className="flex items-start justify-between mb-5">
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-1.5">Investigation Results</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="badge badge-teal">{searchResults.count} snippets analyzed</span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground bg-secondary px-3 py-1.5 rounded-lg">
                        Query: &quot;{searchResults.query}&quot;
                      </div>
                    </div>

                    {/* AI Generated Answer */}
                    <div className="bg-secondary rounded-xl p-6 mb-5 border border-border">
                      <div className="prose prose-sm max-w-none">
                        <div
                          className="text-foreground leading-relaxed"
                          dangerouslySetInnerHTML={{
                            __html: searchResults.answer.replace(/\n/g, '<br/>').replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>').replace(/`(.*?)`/g, '<code class="px-1.5 py-0.5 bg-card rounded-md text-sm font-medium text-primary">$1</code>')
                          }}
                        />
                      </div>
                    </div>

                    {/* Code Snippets */}
                    {searchResults.code_snippets && searchResults.code_snippets.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-foreground mb-3">Relevant Code Snippets</h4>
                        <div className="space-y-3">
                          {searchResults.code_snippets.map((snippet, idx) => (
                            <div key={idx} className="bg-card rounded-xl p-4 border border-border hover:border-primary/30 transition-colors">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <div className="text-sm font-semibold text-foreground">{snippet.service}</div>
                                  <div className="text-xs text-muted-foreground">{snippet.file}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-primary">Rank #{snippet.rank}</span>
                                  <span className="badge badge-teal">
                                    {Math.round(snippet.similarity_score * 100)}% relevant
                                  </span>
                                </div>
                              </div>
                              <div className="text-xs text-muted-foreground mb-2">{snippet.issue}</div>
                              <pre className="text-xs bg-secondary p-3.5 rounded-lg border border-border overflow-x-auto font-mono text-foreground">
                                {snippet.code.split('\n').slice(0, 8).join('\n')}
                                {snippet.code.split('\n').length > 8 && '\n...'}
                              </pre>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Detail Modal */}
                {!showCausalChainAnalysis && selectedNode && (
                  <DetailModal nodeData={selectedNode} onClose={() => setSelectedNode(null)} />
                )}

                {/* Code Editor Section - Only show if we have real triggering change data and not in investigation mode */}
                {!showCausalChainAnalysis && analysis && showCode && analysis.triggering_change && (
                  <div className="card mb-6">
                    <div className="border-b border-border px-4 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-primary font-medium">GitHub</span>
                        <span className="text-sm font-medium text-foreground">
                          {analysis.triggering_change.files_changed?.[0] || 'Source file'}
                        </span>
                        {analysis.triggering_change.github_url && (
                          <a href={analysis.triggering_change.github_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80">
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      <button
                        onClick={() => setShowCode(!showCode)}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Hide
                      </button>
                    </div>
                    <div className="p-4 bg-secondary">
                      <div className="text-xs text-muted-foreground mb-2">
                        Commit {analysis.triggering_change.sha || analysis.triggering_change.id}
                        {analysis.triggering_change.author && ` by ${analysis.triggering_change.author}`}
                      </div>
                      <pre className="text-sm font-mono text-foreground whitespace-pre-wrap">
                        {analysis.triggering_change.message || analysis.triggering_change.description || 'No commit message available'}
                      </pre>
                      {analysis.triggering_change.pr_number && (
                        <div className="mt-3 p-2 bg-warning/10 border border-warning/20 rounded text-xs text-warning">
                          <strong>Related PR:</strong> {analysis.triggering_change.pr_number}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Search and Suggestions */}
                <div className="card p-6">
                  <form onSubmit={handleInvestigate} className="mb-4">
                    <div className="relative flex items-center">
                      <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Ask a question or search... (e.g., 'Why is checkout failing?')"
                        className={`input py-2.5 ${isQuestionQuery(query)
                          ? 'bg-primary/5 border-primary/30 focus:border-primary'
                          : ''
                          }`}
                        style={{ paddingLeft: '2.75rem', paddingRight: '7rem' }}
                        data-testid="search-input"
                      />
                      {isQuestionQuery(query) ? (
                        <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary pointer-events-none" />
                      ) : (
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                      )}
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                        {/* Show AI Analysis button only for non-question queries */}
                        {!isQuestionQuery(query) && (
                          <button
                            type="button"
                            onClick={() => handleCausalChainInvestigation()}
                            disabled={causalChainLoading || !query.trim()}
                            className="btn btn-secondary text-xs py-1.5 px-2.5"
                            title="AI-powered causal chain analysis"
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{causalChainLoading ? 'Analyzing...' : 'AI Analysis'}</span>
                          </button>
                        )}
                        <button
                          type="submit"
                          disabled={(isQuestionQuery(query) ? causalChainLoading : loading) || !query.trim()}
                          className="btn btn-primary text-xs py-1.5 px-3"
                        >
                          {isQuestionQuery(query) && <Sparkles className="w-3.5 h-3.5" />}
                          {isQuestionQuery(query)
                            ? (causalChainLoading ? '...' : 'Ask AI')
                            : (loading ? '...' : 'Search')
                          }
                        </button>
                      </div>
                    </div>
                  </form>

                  {error && (
                    <div className="mb-4 p-3.5 bg-destructive/5 border border-destructive/10 rounded-xl flex items-center gap-2.5 text-destructive text-sm animate-fade-in">
                      <div className="w-5 h-5 rounded-full bg-destructive flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-3.5 h-3.5 text-white" />
                      </div>
                      {error}
                    </div>
                  )}

                  {/* Suggested Fixes */}
                  {analysis && analysis.recommended_actions && (
                    <div className="space-y-3">
                      {analysis.recommended_actions.map((action, idx) => (
                        <div key={idx} className="flex items-start gap-3.5 p-4 bg-secondary rounded-xl border border-border hover:border-primary/30 transition-colors">
                          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                            <Search className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-sm text-foreground mb-1">
                              Suggested Fix: {action.description}
                            </div>
                            {action.command && (
                              <div className="relative mt-2">
                                <pre className="text-xs bg-card border border-border rounded-lg p-3 pr-10 overflow-x-auto font-mono text-foreground">
                                  {action.command}
                                </pre>
                                <button
                                  onClick={() => copyToClipboard(action.command, idx)}
                                  className="absolute top-2.5 right-2.5 p-1.5 hover:bg-secondary rounded-lg transition-colors"
                                  data-testid={`copy-command-${idx}`}
                                >
                                  {copiedCommand === idx ? (
                                    <CheckCircle className="w-4 h-4 text-success" />
                                  ) : (
                                    <Copy className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!analysis && !loading && (
                    <div className="text-center py-10">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                        <Search className="w-6 h-6 text-primary" />
                      </div>
                      <p className="text-sm text-muted-foreground">Enter a query to investigate incidents</p>
                      <button
                        onClick={(e) => {
                          setQuery("What's wrong with checkout service?");
                          handleInvestigate(e);
                        }}
                        className="mt-3 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                      >
                        Try: "What's wrong with checkout service?"
                      </button>
                    </div>
                  )}

                  {loading && (
                    <div className="text-center py-10">
                      <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      </div>
                      <p className="text-sm text-muted-foreground">Analyzing...</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Global History Panel - accessible from sidebar */}
      <HistoryPanel
        isOpen={showGlobalHistory}
        onClose={() => setShowGlobalHistory(false)}
        onRestoreSearch={(item) => {
          // Navigate to search view and set pending restore
          setPendingSearchRestore(item);
          setActiveView('search');
          setShowGlobalHistory(false);
        }}
        onRestoreInvestigation={(item) => {
          // Navigate to investigation detail page
          if (item.id) {
            navigateToInvestigation(item.id, item);
            setShowGlobalHistory(false);
          }
        }}
        onNavigateToInvestigation={(id, item) => {
          navigateToInvestigation(id, item);
          setShowGlobalHistory(false);
        }}
      />
    </div >
  );
}

// Graph visualization component with dynamic data
function GraphVisualization({ analysis, scannerData, onNodeClick }) {
  // Use dynamic data from analysis or show placeholder
  const incidentId = analysis?.incident_id || 'No incident selected';
  const incidentTitle = analysis?.root_cause_summary?.split('.')[0] || 'Pending analysis';
  const rootCause = analysis?.causal_chain?.root_cause || 'Analyzing...';
  const confidence = analysis?.causal_chain?.confidence;
  const triggeringChange = analysis?.triggering_change;

  // Extract scanner nodes
  const scannerAlerts = scannerData?.nodes?.filter(n => n.type === 'alert') || [];
  const scannerRunbooks = scannerData?.nodes?.filter(n => n.type === 'runbook') || [];
  const readinessScore = scannerData?.readiness?.readiness_score;

  // Helper to check if data is sample/fake
  const isSampleData = (value) => {
    if (!value) return true;
    if (typeof value === 'string') {
      return value.startsWith('[SAMPLE]') || value.includes('example.test') || value.includes('acme');
    }
    return false;
  };

  return (
    <div className="relative w-full overflow-hidden bg-secondary/20 rounded-xl border border-border/50" style={{ minHeight: '600px', padding: '20px' }}>

      {/* Background Grid - specific to graph area for technical feel */}
      <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />

      {/* SVG for connection lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
        <defs>
          <marker id="arrowRed" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L0,6 L6,3 z" fill="hsl(var(--destructive))" />
          </marker>
          <marker id="arrowGray" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L0,6 L6,3 z" fill="hsl(var(--muted-foreground))" />
          </marker>
          <marker id="arrowBlue" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L0,6 L6,3 z" fill="hsl(var(--primary))" />
          </marker>

          {/* Gradients */}
          <linearGradient id="gradPath" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--destructive))" stopOpacity="0.5" />
            <stop offset="100%" stopColor="hsl(var(--destructive))" stopOpacity="1" />
          </linearGradient>
        </defs>

        {/* Animated Flow Style */}
        <style>
          {`
            .flow-line {
              stroke-dasharray: 8;
              animation: dash 1s linear infinite;
            }
            @keyframes dash {
              to {
                stroke-dashoffset: -16;
              }
            }
          `}
        </style>

        {/* GitHub to DB Timeouts */}
        <path d="M 240 220 C 320 220 320 240 380 240" stroke="hsl(var(--destructive))" strokeWidth="2" fill="none" markerEnd="url(#arrowRed)" />

        {/* DB Timeouts to Payment Errors */}
        <path d="M 680 260 C 760 260 760 200 820 200" stroke="hsl(var(--destructive))" strokeWidth="2" fill="none" markerEnd="url(#arrowRed)" />

        {/* Jira to Incident (dashed) */}
        <path d="M 760 80 C 480 80 480 90 280 90" stroke="hsl(var(--muted-foreground))" strokeWidth="1.5" strokeDasharray="4,4" fill="none" markerEnd="url(#arrowGray)" />

        {/* Slack to Payment Errors (dashed) */}
        <path d="M 820 320 C 860 320 860 260 900 260" stroke="hsl(var(--muted-foreground))" strokeWidth="1.5" strokeDasharray="4,4" fill="none" />

        {/* Scanner alerts to root cause (if available) - Animated */}
        {scannerAlerts.length > 0 && (
          <path
            className="flow-line"
            d="M 240 450 C 320 450 320 280 380 280"
            stroke="hsl(var(--primary))"
            strokeWidth="2"
            fill="none"
            markerEnd="url(#arrowBlue)"
          />
        )}

        {/* Runbook to root cause (if available) - Animated */}
        {scannerRunbooks.length > 0 && (
          <path
            className="flow-line"
            d="M 240 500 C 350 500 350 350 380 350"
            stroke="hsl(var(--primary))"
            strokeWidth="2"
            fill="none"
            markerEnd="url(#arrowBlue)"
          />
        )}
      </svg>

      {/* Incident Node - Larger, prominent */}
      <div className="absolute transition-all duration-300 ease-spring" style={{ left: '40px', top: '40px', zIndex: 10 }}>
        <div
          onClick={() => onNodeClick({
            type: 'incident',
            id: incidentId,
            title: incidentTitle,
            status: analysis?.status || 'open',
            severity: analysis?.severity || 'unknown',
            description: analysis?.root_cause_summary,
            ...analysis
          })}
          className="card-interactive bg-card border-destructive/20 hover:border-destructive p-5 w-80 shadow-md group border-l-4 border-l-destructive"
          data-testid="node-incident"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-destructive/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <AlertCircle className="w-6 h-6 text-destructive" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold text-destructive tracking-wider uppercase mb-1">
                {(analysis?.severity || 'Active').toUpperCase()} Incident
              </div>
              <div className="font-semibold text-foreground text-sm leading-tight mb-2">{incidentTitle}</div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">{incidentId}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* GitHub Commit - Show if triggering change exists */}
      {triggeringChange && !isSampleData(triggeringChange.author) && (
        <div className="absolute transition-all duration-300 ease-spring" style={{ left: '40px', top: '180px', zIndex: 10 }}>
          <div
            onClick={() => onNodeClick({
              type: 'github',
              title: triggeringChange.message || 'Commit',
              sha: triggeringChange.sha || triggeringChange.id,
              author: triggeringChange.author,
              service: triggeringChange.service,
              files_changed: triggeringChange.files_changed,
              github_url: triggeringChange.github_url,
              pr_number: triggeringChange.pr_number,
              timestamp: triggeringChange.timestamp
            })}
            className="card-interactive bg-card p-4 w-64 shadow-sm border-l-4 border-l-primary"
            data-testid="node-github"
          >
            <div className="flex items-center gap-2.5 mb-2">
              <GitPullRequest className="w-4 h-4 text-primary" />
              <span className="text-xs font-semibold text-primary uppercase tracking-wide">Change Event</span>
            </div>
            <div className="font-mono text-xs text-foreground bg-secondary/50 p-1.5 rounded mb-2 truncate border border-border/50">
              {(triggeringChange.sha || '').substring(0, 7)}
            </div>
            <div className="font-medium text-sm text-foreground truncate mb-1">
              {triggeringChange.message}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <UserIcon className="w-3 h-3" />
              {triggeringChange.author || 'System'}
            </div>
          </div>
        </div>
      )}

      {/* Root Cause - Key node */}
      <div className="absolute transition-all duration-300 ease-spring" style={{ left: '380px', top: '200px', zIndex: 20 }}>
        <div
          onClick={() => onNodeClick({
            type: 'rootcause',
            title: rootCause,
            service: analysis?.causal_chain?.affected_service,
            confidence: confidence,
            evidence: analysis?.causal_chain?.evidence
          })}
          className="card-interactive bg-card p-0 w-96 shadow-xl ring-1 ring-warning/50 overflow-hidden"
          data-testid="node-rootcause"
        >
          <div className="bg-warning/10 px-4 py-2 border-b border-warning/20 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-warning" />
            <span className="text-xs font-bold text-warning uppercase">Root Cause Identified</span>
            {confidence && (
              <span className="ml-auto text-xs font-mono bg-warning/20 text-warning-foreground px-2 py-0.5 rounded-full">
                {Math.round(confidence * 100)}% Conf.
              </span>
            )}
          </div>
          <div className="p-4">
            <h3 className="font-semibold text-foreground text-base mb-1">{rootCause}</h3>
            {analysis?.causal_chain?.affected_service && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                <Database className="w-3 h-3" />
                {analysis.causal_chain.affected_service}
              </div>
            )}

            {readinessScore !== undefined && (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/50">
                <div className="text-xs font-medium text-muted-foreground">Readiness impact:</div>
                <div className="h-1.5 flex-1 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${readinessScore}%` }} />
                </div>
                <div className="text-xs font-bold text-primary">{readinessScore}%</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Downstream Effects */}
      {analysis?.causal_chain?.links?.slice(0, 2).map((link, idx) => (
        <div key={idx} className="absolute transition-all duration-300 ease-spring group" style={{ left: '820px', top: `${180 + idx * 100}px`, zIndex: 10 }}>
          <div className="card-interactive bg-card p-4 w-72 shadow-sm border-l-4 border-l-muted hover:border-l-destructive transition-colors">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground group-hover:text-destructive transition-colors">Downstream Effect</span>
              {link.confidence && <span className="text-[10px] text-muted-foreground">{Math.round(link.confidence * 100)}% Prob.</span>}
            </div>
            <div className="font-medium text-sm text-foreground">{link.target || 'Impacted Service'}</div>
            <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{link.evidence}</div>
          </div>
        </div>
      ))}

      {/* Scanner Info Nodes */}
      {scannerAlerts.slice(0, 2).map((alert, idx) => (
        <div key={alert.id} className="absolute transition-all duration-300 ease-spring" style={{ left: '40px', top: `${500 + idx * 100}px`, zIndex: 10 }}>
          <div
            onClick={() => onNodeClick({ type: 'alert', ...alert })}
            className="card-interactive bg-card p-3 w-64 border-l-4 border-l-primary flex items-center gap-3"
          >
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Zap className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium text-primary mb-0.5">Active Alert</div>
              <div className="text-xs text-foreground truncate">{alert.label}</div>
            </div>
          </div>
        </div>
      ))}

      {/* Suggested Fix Node */}
      <div className="absolute transition-all duration-300 ease-spring" style={{ left: '380px', top: '380px', zIndex: 10 }}>
        <div
          onClick={() => onNodeClick({ type: 'suggestedfix', title: 'Rollback' })}
          className="card-interactive bg-gradient-to-br from-success/5 to-transparent border-success/30 p-4 w-80 shadow-md ring-1 ring-success/20 hover:ring-success/40"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-success text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-success/30">
              <CheckCircle className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-success uppercase mb-1">Recommended Action</div>
              <div className="font-medium text-sm text-foreground">Rollback recent configuration</div>
              <div className="text-xs text-muted-foreground mt-1 font-mono bg-white/50 px-1.5 py-0.5 rounded inline-block">
                git revert 7a3f9e2
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Connector Labels */}
      <div className="absolute bg-card border border-border px-2 py-0.5 rounded-full shadow-sm text-[10px] font-medium text-muted-foreground" style={{ left: '310px', top: '230px' }}>
        Triggered
      </div>
      <div className="absolute bg-card border border-border px-2 py-0.5 rounded-full shadow-sm text-[10px] font-medium text-muted-foreground" style={{ left: '740px', top: '250px' }}>
        Impacted
      </div>

    </div>
  );
}

function App() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background bg-grid flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 rounded-xl bg-primary flex items-center justify-center mx-auto">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
          </div>
          <p className="mt-5 text-muted-foreground font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  return <MainApp />;
}

export default App;
